import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, Check, AlertCircle, Building, User, Calendar, DollarSign } from 'lucide-react';
import api from '../../api/apiClient';

interface Property {
  id: number;
  address: string;
  type: string;
  rentAmount?: number;
  rentFrequency?: string;
  ownerName?: string;
  ownerId?: number;
  status?: string;
  currentAgentId?: number;
}

interface Tenant {
  id: number;
  name: string;
  email: string;
}

interface Owner {
  id: number;
  name: string;
  email: string;
}

export default function AgentCreateLease() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('');

  // Set default dates (today + 12 months)
  const today = new Date().toISOString().split('T')[0];
  const oneYearLater = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [form, setForm] = useState({
    propertyId: '',
    tenantId: '',
    tenantEmail: '',
    startDate: today,
    endDate: oneYearLater,
    rentAmount: '',
    rentFrequency: 'WEEKLY',
    securityDeposit: '',
    terms: '',
  });

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Load properties when owner is selected
  useEffect(() => {
    if (selectedOwnerId) {
      loadPropertiesByOwner(selectedOwnerId);
    } else {
      loadAgentProperties();
    }
  }, [selectedOwnerId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load properties assigned to the agent
      const propsResponse = await api.get<any>('/agent/properties');
      const propsList = Array.isArray(propsResponse) ? propsResponse : (propsResponse.content || []);
      setProperties(propsList);

      // Load tenants (renters)
      const tenantsResponse = await api.get<any>('/users?role=RENTER');
      const tenantsList = Array.isArray(tenantsResponse) ? tenantsResponse : (tenantsResponse.content || []);
      setTenants(tenantsList);

      // Load owners
      const ownersResponse = await api.get<any>('/users?role=OWNER');
      const ownersList = Array.isArray(ownersResponse) ? ownersResponse : (ownersResponse.content || []);
      setOwners(ownersList);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAgentProperties = async () => {
    try {
      const propsResponse = await api.get<any>('/agent/properties');
      const propsList = Array.isArray(propsResponse) ? propsResponse : (propsResponse.content || []);
      setProperties(propsList);
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  };

  const loadPropertiesByOwner = async (ownerId: string) => {
    try {
      const propsResponse = await api.get<any>(`/agent/properties?ownerId=${ownerId}`);
      const propsList = Array.isArray(propsResponse) ? propsResponse : (propsResponse.content || []);
      setProperties(propsList);
      // Reset property selection when owner changes
      setForm(prev => ({ ...prev, propertyId: '' }));
      setSelectedProperty(null);
    } catch (err) {
      console.error('Failed to load owner properties:', err);
    }
  };

  const handleOwnerChange = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
  };

  const handlePropertyChange = (propertyId: string) => {
    setForm({ ...form, propertyId });
    const property = properties.find(p => p.id.toString() === propertyId);
    setSelectedProperty(property || null);

    if (property) {
      // Auto-fill rent details from property
      setForm(prev => ({
        ...prev,
        propertyId,
        rentAmount: property.rentAmount?.toString() || '',
        rentFrequency: property.rentFrequency || 'WEEKLY',
      }));

      // Check if approval is needed (property managed by another agent)
      if (property.currentAgentId && property.status === 'OCCUPIED') {
        setApprovalRequired(true);
      } else {
        setApprovalRequired(false);
      }
    }
  };

  const handleTenantChange = (tenantId: string) => {
    const tenant = tenants.find(t => t.id.toString() === tenantId);
    setForm(prev => ({
      ...prev,
      tenantId,
      tenantEmail: tenant?.email || '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.propertyId) {
      setError('Please select a property');
      return;
    }

    if (!form.tenantId && !form.tenantEmail) {
      setError('Please select a tenant or enter tenant email');
      return;
    }

    if (!form.startDate || !form.endDate) {
      setError('Please enter lease dates');
      return;
    }

    if (!form.rentAmount) {
      setError('Please enter rent amount');
      return;
    }

    setSaving(true);
    try {
      const data = {
        propertyId: parseInt(form.propertyId),
        tenantId: form.tenantId ? parseInt(form.tenantId) : null,
        tenantEmail: form.tenantEmail,
        startDate: form.startDate,
        endDate: form.endDate,
        rentAmount: parseFloat(form.rentAmount),
        rentFrequency: form.rentFrequency,
        securityDeposit: form.securityDeposit ? parseFloat(form.securityDeposit) : null,
        terms: form.terms,
        updateProperty: true, // Update property with new rent details if changed
      };

      await api.post('/agent/leases', data);

      // Also update the property with new rent details if changed
      if (selectedProperty && (
        parseFloat(form.rentAmount) !== selectedProperty.rentAmount ||
        form.rentFrequency !== selectedProperty.rentFrequency
      )) {
        await api.put(`/agent/properties/${form.propertyId}`, {
          rentAmount: parseFloat(form.rentAmount),
          rentFrequency: form.rentFrequency,
        });
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/agent/leases');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create lease');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Lease Created!</h2>
          <p className="text-gray-600">
            {approvalRequired
              ? 'An approval request has been sent to the property owner.'
              : 'The lease has been created successfully.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/agent/leases')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create Lease</h1>
            <p className="text-gray-600">Set up a new lease agreement</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {approvalRequired && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <span className="text-yellow-800">
            This property is currently in an active lease. An approval request will be sent to the owner.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Property Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Property</h2>
          </div>

          <div className="space-y-4">
            {/* Owner filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Owner</label>
              <select
                value={selectedOwnerId}
                onChange={(e) => handleOwnerChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">All my properties</option>
                {owners.map(owner => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name || owner.email}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                {selectedOwnerId ? "Showing properties owned by this owner" : "Showing properties assigned to you"}
              </p>
            </div>

            {/* Property selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Property *</label>
              <select
                value={form.propertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              >
                <option value="">Choose a property...</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.address} ({property.type || 'Property'})
                    {property.status === 'OCCUPIED' ? ' - Occupied' : ''}
                  </option>
                ))}
              </select>
              {properties.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {selectedOwnerId
                    ? "This owner has no properties. "
                    : "No properties assigned to you. "}
                  <a href="/agent/properties/new" className="text-secondary hover:text-primary">Add a property</a>
                </p>
              )}
            </div>
          </div>

          {selectedProperty && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Owner:</strong> {selectedProperty.ownerName || 'Unknown'}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Current Rent:</strong> ${selectedProperty.rentAmount?.toLocaleString() || 'Not set'} / {selectedProperty.rentFrequency?.toLowerCase().replace('ly', '') || 'week'}
              </p>
            </div>
          )}
        </div>

        {/* Tenant Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Existing Tenant</label>
              <select
                value={form.tenantId}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="">Choose a tenant or enter email below...</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name || tenant.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-center text-gray-500 text-sm">- OR -</div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite New Tenant by Email</label>
              <input
                type="email"
                value={form.tenantEmail}
                onChange={(e) => setForm({ ...form, tenantEmail: e.target.value, tenantId: '' })}
                placeholder="tenant@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">An invitation will be sent to this email.</p>
            </div>
          </div>
        </div>

        {/* Lease Terms */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Lease Period</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                min={form.startDate}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>
          </div>
        </div>

        {/* Rent Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Rent Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rent Amount ($) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.rentAmount}
                onChange={(e) => setForm({ ...form, rentAmount: e.target.value })}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
              {selectedProperty && form.rentAmount && parseFloat(form.rentAmount) !== selectedProperty.rentAmount && (
                <p className="text-sm text-orange-600 mt-1">
                  ⚠️ This differs from property rent. Property will be updated.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rent Frequency *</label>
              <select
                value={form.rentFrequency}
                onChange={(e) => setForm({ ...form, rentFrequency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              >
                <option value="WEEKLY">Weekly</option>
                <option value="FORTNIGHTLY">Fortnightly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Security Deposit ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.securityDeposit}
                onChange={(e) => setForm({ ...form, securityDeposit: e.target.value })}
                placeholder="Optional"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Additional Terms */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Terms</h2>
          <textarea
            value={form.terms}
            onChange={(e) => setForm({ ...form, terms: e.target.value })}
            rows={4}
            placeholder="Any additional terms or conditions..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate('/agent/leases')}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 font-medium"
          >
            {saving ? 'Creating...' : approvalRequired ? 'Submit for Approval' : 'Create Lease'}
          </button>
        </div>
      </form>
    </div>
  );
}


